import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { opsFetch } from "../lib/api";
import { Count, Empty, Pill } from "../components/ui";

type Item = {
  kind: "question" | "post";
  id: string;
  title: string;
  body: string;
  status: string;
  user_id: string;
  created_at?: string | null;
};

type QueueRes = { data: { items: Item[]; total: number } };

export function UgcPage() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("pending");
  const q = useQuery({
    queryKey: ["ops-ugc", status],
    queryFn: () => opsFetch<QueueRes>(`/api/ops/ugc/queue?status=${encodeURIComponent(status)}&per_page=50`),
  });

  const moderate = useMutation({
    mutationFn: ({ kind, id, next }: { kind: string; id: string; next: string }) =>
      opsFetch(`/api/ops/ugc/${kind === "question" ? "questions" : "posts"}/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify({ status: next }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["ops-ugc"] }),
  });

  return (
    <>
      <div className="ops-toolbar">
        <select className="ops-input" onChange={(e) => setStatus(e.target.value)} value={status}>
          <option value="pending">pending</option>
          <option value="approved">approved</option>
          <option value="rejected">rejected</option>
        </select>
        {q.data ? <Count label="items" n={q.data.data.total} /> : null}
      </div>
      {q.isLoading ? <p className="ops-muted">Loading…</p> : null}
      {q.isError ? <p className="ops-error">{(q.error as Error).message}</p> : null}
      {q.data?.data.items.length === 0 ? <Empty>Nothing in this queue.</Empty> : null}
      {q.data?.data.items.map((item) => (
        <article className="ops-panel" key={`${item.kind}-${item.id}`}>
          <div className="ops-toolbar">
            <strong>{item.title}</strong>
            <Pill value={item.kind} />
            <Pill value={item.status} />
            <span className="ops-muted">{waiting(item.created_at)}</span>
          </div>
          <p className="ops-review__body">{item.body}</p>
          <div className="ops-toolbar">
            {item.status !== "approved" ? (
              <button
                className="ops-btn ops-btn--primary"
                disabled={moderate.isPending}
                onClick={() => moderate.mutate({ kind: item.kind, id: item.id, next: "approved" })}
                type="button"
              >
                Approve
              </button>
            ) : null}
            {item.status !== "rejected" ? (
              <button
                className="ops-btn ops-btn--ghost"
                disabled={moderate.isPending}
                onClick={() => moderate.mutate({ kind: item.kind, id: item.id, next: "rejected" })}
                type="button"
              >
                Reject
              </button>
            ) : null}
          </div>
        </article>
      ))}
    </>
  );
}

function waiting(iso?: string | null) {
  if (!iso) return "";
  const hours = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 3600000));
  if (hours < 1) return "under an hour";
  if (hours < 48) return `${hours}h waiting`;
  return `${Math.floor(hours / 24)}d waiting`;
}
